import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Only bundle core languages statically (HR + EN ~500KB instead of ~6.5MB for all 29)
import hr from './locales/hr.json';
import en from './locales/en.json';
import { SSG_LANGUAGE } from '@/lib/ssgHydration';

// Language configuration with metadata.
//
// `flag` is the emoji regional indicator. It looks great on macOS/iOS/Android
// but Windows refuses to render it (Segoe UI Emoji ships no glyphs for the
// regional indicator pairs), so on Windows users see plain ISO letters like
// "HR" "GB". We keep the field for backward compat (translation.ts, tests,
// JSON exports), but the UI now renders flags through <Flag country={...} />
// which uses the `flag-icons` SVG sprite via the `country` ISO-3166 alpha-2
// code below.
//
// Sorted by priority: 1. Croatian tourists, 2. EU tourists, 3. World tourists
export const SUPPORTED_LANGUAGES = [
  // Main languages
  { code: 'hr', country: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷', region: 'main' },
  { code: 'en', country: 'gb', name: 'English', nativeName: 'English', flag: '🇬🇧', region: 'main' },

  // European - Top Croatia tourists (sorted by % of tourists)
  { code: 'de', country: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', region: 'europe' },
  { code: 'sl', country: 'si', name: 'Slovenian', nativeName: 'Slovenščina', flag: '🇸🇮', region: 'europe' },
  { code: 'pl', country: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', region: 'europe' },
  { code: 'cs', country: 'cz', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿', region: 'europe' },
  { code: 'it', country: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', region: 'europe' },
  { code: 'hu', country: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺', region: 'europe' },
  { code: 'sk', country: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰', region: 'europe' },
  { code: 'nl', country: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', region: 'europe' },
  { code: 'fr', country: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', region: 'europe' },
  { code: 'es', country: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', region: 'europe' },
  { code: 'sv', country: 'se', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', region: 'europe' },
  { code: 'da', country: 'dk', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰', region: 'europe' },
  { code: 'no', country: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴', region: 'europe' },
  { code: 'fi', country: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮', region: 'europe' },
  { code: 'pt', country: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', region: 'europe' },
  { code: 'ro', country: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴', region: 'europe' },
  { code: 'el', country: 'gr', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷', region: 'europe' },
  { code: 'tr', country: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', region: 'europe' },

  // World languages (sorted by tourism spending)
  { code: 'ru', country: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', region: 'world' },
  { code: 'zh', country: 'cn', name: 'Chinese', nativeName: '简体中文', flag: '🇨🇳', region: 'world' },
  { code: 'ja', country: 'jp', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', region: 'world' },
  { code: 'ko', country: 'kr', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', region: 'world' },
  { code: 'ar', country: 'sa', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', region: 'world', rtl: true },
  { code: 'he', country: 'il', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱', region: 'world', rtl: true },
  { code: 'th', country: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', region: 'world' },
  { code: 'vi', country: 'vn', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', region: 'world' },
  { code: 'id', country: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩', region: 'world' },
] as const;

export type LanguageCode = typeof SUPPORTED_LANGUAGES[number]['code'];

// Helper to get language info
export const getLanguageInfo = (code: string) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code);
};

// Helper to check if language is RTL
export const isRTL = (code: string): boolean => {
  const lang = getLanguageInfo(code);
  return lang && 'rtl' in lang ? lang.rtl === true : false;
};

// Get languages by region
export const getLanguagesByRegion = (region: 'main' | 'europe' | 'world') => {
  return SUPPORTED_LANGUAGES.filter(lang => lang.region === region);
};

// All language codes for validation
export const LANGUAGE_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

// Languages that have a complete translation bundle. Used by the guest portal
// to decide whether to honour the guest's preferred language or fall back to
// English. Add codes here once that locale's JSON file is fully translated.
export const FULLY_TRANSLATED_LANGUAGES = ['hr', 'en'] as const;
export type FullyTranslatedLanguage = (typeof FULLY_TRANSLATED_LANGUAGES)[number];

export function isFullyTranslated(code: string | null | undefined): code is FullyTranslatedLanguage {
  return !!code && (FULLY_TRANSLATED_LANGUAGES as readonly string[]).includes(code);
}

// Dynamic import map for non-core languages (loaded on demand)
const LAZY_LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  de: () => import('./locales/de.json'),
  sl: () => import('./locales/sl.json'),
  pl: () => import('./locales/pl.json'),
  cs: () => import('./locales/cs.json'),
  it: () => import('./locales/it.json'),
  hu: () => import('./locales/hu.json'),
  sk: () => import('./locales/sk.json'),
  nl: () => import('./locales/nl.json'),
  fr: () => import('./locales/fr.json'),
  es: () => import('./locales/es.json'),
  sv: () => import('./locales/sv.json'),
  da: () => import('./locales/da.json'),
  no: () => import('./locales/no.json'),
  fi: () => import('./locales/fi.json'),
  pt: () => import('./locales/pt.json'),
  ro: () => import('./locales/ro.json'),
  el: () => import('./locales/el.json'),
  tr: () => import('./locales/tr.json'),
  ru: () => import('./locales/ru.json'),
  zh: () => import('./locales/zh.json'),
  ja: () => import('./locales/ja.json'),
  ko: () => import('./locales/ko.json'),
  ar: () => import('./locales/ar.json'),
  he: () => import('./locales/he.json'),
  th: () => import('./locales/th.json'),
  vi: () => import('./locales/vi.json'),
  id: () => import('./locales/id.json'),
};

// Track which languages have been loaded
const loadedLanguages = new Set<string>(['hr', 'en']);

// Load a language dynamically (returns immediately if already loaded)
export async function loadLanguage(code: string): Promise<void> {
  if (loadedLanguages.has(code)) return;

  const loader = LAZY_LOADERS[code];
  if (!loader) return;

  try {
    const module = await loader();
    i18n.addResourceBundle(code, 'translation', module.default, true, true);
    loadedLanguages.add(code);
  } catch (err) {
    console.error(`Failed to load language: ${code}`, err);
  }
}

// Get saved language or detect from browser
const getSavedLanguage = (): string => {
  // Prerendering (SSG): a fixed language. Node's `navigator.language` is the
  // build machine's locale, so the static pages came out in Croatian on a
  // Croatian PC and in English on CI. ssgHydration.ts relies on this value.
  if (import.meta.env.SSR) return SSG_LANGUAGE;

  // Storage can throw (blocked third-party storage, e.g. the calendar embed
  // inside a host's website in Safari) — the app must still start.
  let saved: string | null = null;
  try {
    saved = localStorage.getItem('bookiapp-language');
  } catch {
    saved = null;
  }
  if (saved && LANGUAGE_CODES.includes(saved as LanguageCode)) {
    return saved;
  }

  // Detect from browser
  const browserLang = navigator.language.split('-')[0];
  if (LANGUAGE_CODES.includes(browserLang as LanguageCode)) {
    return browserLang;
  }

  // Default to English for international users
  return 'en';
};

const initialLang = getSavedLanguage();

i18n.use(initReactI18next).init({
  resources: {
    hr: { translation: hr },
    en: { translation: en },
  },
  lng: initialLang,
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

// If initial language is not hr/en, load it immediately
if (initialLang !== 'hr' && initialLang !== 'en') {
  loadLanguage(initialLang);
}

// Save language preference, handle RTL, and lazy-load on change
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('bookiapp-language', lng);
  document.documentElement.lang = lng;

  // Handle RTL languages
  if (isRTL(lng)) {
    document.documentElement.dir = 'rtl';
  } else {
    document.documentElement.dir = 'ltr';
  }

  // Load language bundle if not already loaded
  loadLanguage(lng);
});

export default i18n;
